import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  UserPlus, Mail, Shield, RotateCw, KeyRound, Power, Trash2, CheckCircle2, Clock,
} from "lucide-react";

const fmtWhen = (iso) => {
  if (!iso) return "Never";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

export default function TeamAccess() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [members, setMembers] = useState([]);
  const [catalog, setCatalog] = useState({ permissions: [], presets: [] });
  const [loading, setLoading] = useState(true);

  // Invite dialog state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invName, setInvName] = useState("");
  const [invEmail, setInvEmail] = useState("");
  const [invPerms, setInvPerms] = useState([]);
  const [inviting, setInviting] = useState(false);

  // Edit permissions dialog state
  const [editTarget, setEditTarget] = useState(null);
  const [editPerms, setEditPerms] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([
        api.get("/team/members"),
        api.get("/team/permissions"),
      ]);
      setMembers(m.data || []);
      setCatalog(p.data || { permissions: [], presets: [] });
    } catch (e) {
      toast.error(errMsg(e, "Could not load team"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);

  const openInvite = () => {
    setInvName("");
    setInvEmail("");
    setInvPerms([]);
    setInviteOpen(true);
  };

  const togglePerm = (list, setList) => (key) => {
    setList(list.includes(key) ? list.filter((p) => p !== key) : [...list, key]);
  };

  const applyPreset = (list, setList) => (perms) => {
    setList([...perms]);
  };

  const submitInvite = async () => {
    const email = invEmail.trim().toLowerCase();
    const name = invName.trim();
    if (!name) return toast.error("Enter a name");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Enter a valid email");
    setInviting(true);
    try {
      await api.post("/team/members", { email, name, permissions: invPerms });
      toast.success(`Invite sent to ${email}`);
      setInviteOpen(false);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not send invite"));
    } finally {
      setInviting(false);
    }
  };

  const openEdit = (m) => {
    setEditTarget(m);
    setEditPerms(m.permissions || []);
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    setSavingEdit(true);
    try {
      await api.patch(`/team/members/${editTarget.user_id}`, { permissions: editPerms });
      toast.success("Permissions updated");
      setEditTarget(null);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not update"));
    } finally {
      setSavingEdit(false);
    }
  };

  const resend = async (m) => {
    try {
      const r = await api.post(`/team/members/${m.user_id}/resend-invite`);
      toast.success(r.data?.kind === "reset" ? "Password reset link sent" : "Invite resent");
    } catch (e) {
      toast.error(errMsg(e, "Could not resend"));
    }
  };

  const resetPassword = async (m) => {
    if (!window.confirm(`Email ${m.name} a one-click password reset link?`)) return;
    try {
      await api.post(`/team/members/${m.user_id}/reset-password`);
      toast.success("Password reset link sent");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not send reset"));
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/team/members/${m.user_id}`, { active: !m.active });
      toast.success(m.active ? "Account deactivated" : "Account reactivated");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not update"));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.name}'s account? They'll immediately lose access.`)) return;
    try {
      await api.delete(`/team/members/${m.user_id}`);
      toast.success("Teammate removed");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not delete"));
    }
  };

  const admin = members.find((m) => m.role === "admin");
  const staff = members.filter((m) => m.role === "staff");

  if (!isAdmin) {
    return (
      <div className="space-y-6" data-testid="team-page-staff">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Team &amp; Access</h2>
          <p className="text-sm text-slate-500 mt-0.5">Your signed-in account.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="eyebrow mb-3">Signed in as</div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-slate-900 grid place-items-center text-white font-bold">
              {(user?.name || user?.username || "?").slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="font-semibold text-slate-900">{user?.name || user?.username}</div>
              <div className="text-xs text-slate-500">Role: <span className="pill-blue">{user?.role}</span></div>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="eyebrow mb-3">Your permissions</div>
          <div className="flex flex-wrap gap-2">
            {(user?.permissions || []).length === 0 && (
              <span className="text-xs text-slate-500">No workspace permissions assigned. Ask your admin.</span>
            )}
            {(user?.permissions || []).map((p) => (
              <span key={p} className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded border border-emerald-200 text-xs font-medium capitalize">
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Shield size={22} /> Team &amp; Access
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Invite your office staff and choose exactly what they can do inside your workspace.
          </p>
        </div>
        <Button
          onClick={openInvite}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="invite-staff-btn"
        >
          <UserPlus size={16} className="mr-1.5" /> Invite teammate
        </Button>
      </div>

      {/* Admin (you) */}
      {admin && (
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="eyebrow mb-3">Workspace owner</div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-slate-900 grid place-items-center text-white font-bold">
                {(admin.name || admin.username || "?").slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div className="font-semibold text-slate-900">{admin.name || admin.username}</div>
                <div className="text-xs text-slate-500">{admin.email || "No email on file"}</div>
              </div>
            </div>
            <span className="pill-blue capitalize">Admin · full access</span>
          </div>
        </div>
      )}

      {/* Staff list */}
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold text-slate-900">Office staff <span className="text-xs text-slate-500 font-normal">({staff.length})</span></div>
        </div>

        {loading ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">Loading...</div>
        ) : staff.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">
            No staff accounts yet.
            <div className="mt-3">
              <Button variant="outline" onClick={openInvite} data-testid="invite-empty-btn">
                <UserPlus size={14} className="mr-1" /> Invite your first teammate
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-slate-100">
              {staff.map((m) => (
                <StaffCard
                  key={m.user_id}
                  m={m}
                  onEdit={() => openEdit(m)}
                  onResend={() => resend(m)}
                  onResetPassword={() => resetPassword(m)}
                  onToggleActive={() => toggleActive(m)}
                  onDelete={() => remove(m)}
                />
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 border-t border-slate-200">
                    <th className="px-5 py-2.5">Name</th>
                    <th className="px-5 py-2.5">Email</th>
                    <th className="px-5 py-2.5">Permissions</th>
                    <th className="px-5 py-2.5">Status</th>
                    <th className="px-5 py-2.5">Last signed in</th>
                    <th className="px-5 py-2.5 text-right w-[260px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.map((m) => (
                    <StaffRow
                      key={m.user_id}
                      m={m}
                      onEdit={() => openEdit(m)}
                      onResend={() => resend(m)}
                      onResetPassword={() => resetPassword(m)}
                      onToggleActive={() => toggleActive(m)}
                      onDelete={() => remove(m)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={(o) => !o && setInviteOpen(false)}>
        <DialogContent
          className="max-w-lg p-0 gap-0 max-h-[85vh] overflow-y-auto"
          data-testid="invite-dialog"
        >
          <DialogHeader className="sticky top-0 z-10 bg-white p-6 pb-3 border-b border-slate-100 text-left">
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={18} className="text-blue-600" /> Invite a teammate
            </DialogTitle>
            <DialogDescription>
              They'll get an email with a one-click link to set their password and sign in.
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 py-4 space-y-4">
            <div>
              <Label>Full name</Label>
              <Input
                value={invName}
                onChange={(e) => setInvName(e.target.value)}
                placeholder="Alex Rivera"
                autoFocus
                data-testid="invite-name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={invEmail}
                onChange={(e) => setInvEmail(e.target.value)}
                placeholder="alex@yourcompany.com"
                data-testid="invite-email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Permissions</Label>
                <span className="text-[11px] text-slate-500">{invPerms.length} selected</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {catalog.presets.map((pr) => (
                  <button
                    key={pr.key}
                    type="button"
                    onClick={() => applyPreset(invPerms, setInvPerms)(pr.permissions)}
                    className="px-2.5 py-1 text-[11px] font-medium rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                    data-testid={`preset-${pr.key}`}
                  >
                    {pr.label}
                  </button>
                ))}
                {invPerms.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setInvPerms([])}
                    className="px-2.5 py-1 text-[11px] font-medium rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-500"
                  >
                    Clear
                  </button>
                )}
              </div>
              <PermissionGrid
                options={catalog.permissions}
                selected={invPerms}
                onToggle={togglePerm(invPerms, setInvPerms)}
                testPrefix="inv-perm"
              />
            </div>
          </div>
          <DialogFooter className="sticky bottom-0 z-10 bg-slate-50 p-4 border-t border-slate-100 flex-row justify-end gap-2">
            <Button variant="ghost" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button
              onClick={submitInvite}
              disabled={inviting}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="invite-submit-btn"
            >
              <Mail size={14} className="mr-1" /> {inviting ? "Sending..." : "Send invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit permissions dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent
          className="max-w-lg p-0 gap-0 max-h-[85vh] overflow-y-auto"
          data-testid="edit-perms-dialog"
        >
          <DialogHeader className="sticky top-0 z-10 bg-white p-6 pb-3 border-b border-slate-100 text-left">
            <DialogTitle className="flex items-center gap-2">
              <Shield size={18} className="text-blue-600" /> Edit permissions
            </DialogTitle>
            <DialogDescription>
              {editTarget?.name} · {editTarget?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 py-4 space-y-3">
            <div className="flex flex-wrap gap-1.5 mb-1">
              {catalog.presets.map((pr) => (
                <button
                  key={pr.key}
                  type="button"
                  onClick={() => setEditPerms([...pr.permissions])}
                  className="px-2.5 py-1 text-[11px] font-medium rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                >
                  {pr.label}
                </button>
              ))}
              {editPerms.length > 0 && (
                <button
                  type="button"
                  onClick={() => setEditPerms([])}
                  className="px-2.5 py-1 text-[11px] font-medium rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-500"
                >
                  Clear
                </button>
              )}
            </div>
            <PermissionGrid
              options={catalog.permissions}
              selected={editPerms}
              onToggle={togglePerm(editPerms, setEditPerms)}
              testPrefix="edit-perm"
            />
          </div>
          <DialogFooter className="sticky bottom-0 z-10 bg-slate-50 p-4 border-t border-slate-100 flex-row justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button
              onClick={saveEdit}
              disabled={savingEdit}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="edit-perms-save"
            >
              {savingEdit ? "Saving..." : "Save permissions"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PermissionGrid({ options, selected, onToggle, testPrefix }) {
  return (
    <div className="grid grid-cols-1 gap-1.5">
      {options.map((opt) => {
        const checked = selected.includes(opt.key);
        return (
          <label
            key={opt.key}
            className={`flex items-start gap-3 rounded border px-3 py-2 cursor-pointer ${
              checked ? "border-blue-300 bg-blue-50/60" : "border-slate-200 bg-white hover:bg-slate-50"
            }`}
            data-testid={`${testPrefix}-${opt.key}`}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => onToggle(opt.key)}
              className="mt-0.5"
            />
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900">{opt.label}</div>
              <div className="text-[11px] text-slate-500">{opt.desc}</div>
            </div>
          </label>
        );
      })}
    </div>
  );
}

function StaffStatusPill({ m }) {
  if (m.pending_invite) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-200 bg-amber-100 text-amber-800 text-[10px] font-semibold">
        <Clock size={11} /> Pending invite
      </span>
    );
  }
  if (m.active === false) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-300 bg-slate-100 text-slate-700 text-[10px] font-semibold">
        Deactivated
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-100 text-emerald-800 text-[10px] font-semibold">
      <CheckCircle2 size={11} /> Active
    </span>
  );
}

function StaffRow({ m, onEdit, onResend, onResetPassword, onToggleActive, onDelete }) {
  return (
    <tr className="border-t border-slate-100" data-testid={`staff-row-${m.user_id}`}>
      <td className="px-5 py-3 align-top">
        <div className="font-medium text-slate-900">{m.name || m.username}</div>
        <div className="text-[11px] text-slate-500 font-mono">{m.username}</div>
      </td>
      <td className="px-5 py-3 align-top text-slate-700">{m.email || "—"}</td>
      <td className="px-5 py-3 align-top">
        {(m.permissions || []).length === 0 ? (
          <span className="text-xs text-slate-400">None</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {(m.permissions || []).map((p) => (
              <span key={p} className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-medium capitalize text-slate-700">
                {p}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-5 py-3 align-top"><StaffStatusPill m={m} /></td>
      <td className="px-5 py-3 align-top text-xs text-slate-600">{fmtWhen(m.last_sign_in_at)}</td>
      <td className="px-5 py-3 align-top">
        <div className="flex items-center justify-end gap-1 flex-wrap">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onEdit} data-testid={`edit-perms-${m.user_id}`}>
            <Shield size={12} className="mr-1" /> Permissions
          </Button>
          {m.pending_invite ? (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onResend} data-testid={`resend-${m.user_id}`}>
              <RotateCw size={12} className="mr-1" /> Resend invite
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onResetPassword} data-testid={`reset-${m.user_id}`}>
              <KeyRound size={12} className="mr-1" /> Reset password
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className={`h-7 text-xs ${m.active ? "" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}
            onClick={onToggleActive}
            data-testid={`toggle-active-${m.user_id}`}
          >
            <Power size={12} className="mr-1" /> {m.active ? "Deactivate" : "Reactivate"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
            onClick={onDelete}
            data-testid={`delete-${m.user_id}`}
          >
            <Trash2 size={12} className="mr-1" /> Delete
          </Button>
        </div>
      </td>
    </tr>
  );
}

function StaffCard({ m, onEdit, onResend, onResetPassword, onToggleActive, onDelete }) {
  return (
    <div className="p-4 space-y-3" data-testid={`staff-card-${m.user_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate">{m.name || m.username}</div>
          <div className="text-xs text-slate-500 truncate">{m.email}</div>
          <div className="text-[10px] text-slate-400 font-mono">{m.username}</div>
        </div>
        <StaffStatusPill m={m} />
      </div>
      <div className="text-xs text-slate-500">
        <span className="text-slate-400">Last signed in:</span> {fmtWhen(m.last_sign_in_at)}
      </div>
      <div className="flex flex-wrap gap-1">
        {(m.permissions || []).length === 0 && (
          <span className="text-[11px] text-slate-400">No permissions</span>
        )}
        {(m.permissions || []).map((p) => (
          <span key={p} className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-medium capitalize text-slate-700">
            {p}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
        <Button size="sm" variant="outline" className="h-9 text-xs" onClick={onEdit}>
          <Shield size={12} className="mr-1" /> Permissions
        </Button>
        {m.pending_invite ? (
          <Button size="sm" variant="outline" className="h-9 text-xs" onClick={onResend}>
            <RotateCw size={12} className="mr-1" /> Resend
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="h-9 text-xs" onClick={onResetPassword}>
            <KeyRound size={12} className="mr-1" /> Reset password
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-9 text-xs" onClick={onToggleActive}>
          <Power size={12} className="mr-1" /> {m.active ? "Deactivate" : "Reactivate"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-9 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
          onClick={onDelete}
        >
          <Trash2 size={12} className="mr-1" /> Delete
        </Button>
      </div>
    </div>
  );
}
